import { cloneDeep } from 'lodash';

import {
    CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR,
    CLOSE_EXPERIMENTS_EDITOR,
    DESELECT_EXPERIMENT_TREE_NODE,
    EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR,
    ExperimentsEditorAction,
    OPEN_EXPERIMENTS_EDITOR,
    SELECT_EXPERIMENT_TREE_NODE,
    TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR,
    TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION,
    TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR
} from '../actions/experimentsEditor';
import { mapCorrectionsToEditor } from '../mappers/experimentsEditor';
import {
    enableExperiments,
    enableSiblingSegments,
    getDisabledExperimentsMapIfLimit,
    getDisabledSegmentsMapByExperiment,
    getInitialExperimentMaps
} from '../service/ExperimentsEditorHelpers/ExperimentsEditorHelpers';
import { validateRateCorrectionValue } from '../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { TreeHelpers } from '../service/TreeHelpers/TreeHelpers';
import { ExperimentsEditorState } from '../typings/experimentsEditor';
import { State } from '../typings/state';

export const experimentsEditorInitialState = {
    isOpen: false,
    areCorrectionsEnabled: false,
    isSegmentTargetingEnabled: false,
    searchValue: '',
    selectedMap: {},
    expandedMap: {},
    filteredMap: {},
    disabledMap: {},
    correctionsMap: {
        value: {},
        errors: []
    }
};

export function experimentsEditor(
    state: ExperimentsEditorState = {},
    action: ExperimentsEditorAction,
    fullState: State
): ExperimentsEditorState {
    switch (action.type) {
        case OPEN_EXPERIMENTS_EDITOR: {
            const { searchValue, expandedMap } = experimentsEditorInitialState;
            const {
                experimentsTree,
                selectedMap,
                areCorrectionsEnabled,
                isSegmentTargetingEnabled,
                correctionsMap
            } = action.initialValue;
            const { filteredMap, disabledMap } = getInitialExperimentMaps(
                experimentsTree,
                selectedMap,
                action.activeExperimentsLimit
            );

            return {
                ...state,
                [action.editorId]: {
                    searchValue,
                    expandedMap,
                    selectedMap,
                    areCorrectionsEnabled,
                    isSegmentTargetingEnabled,
                    filteredMap,
                    disabledMap,
                    correctionsMap: mapCorrectionsToEditor(correctionsMap),
                    isOpen: true
                }
            };
        }

        case CLOSE_EXPERIMENTS_EDITOR: {
            return {
                ...state,
                [action.editorId]: {
                    ...experimentsEditorInitialState,
                    isOpen: false
                }
            };
        }

        case TOGGLE_SEGMENT_TARGETING_EXPERIMENTS_EDITOR: {
            const { editorId } = action;
            const currentEditorState =
                state[editorId] || experimentsEditorInitialState;

            return {
                ...state,
                [editorId]: {
                    ...currentEditorState,
                    isSegmentTargetingEnabled: !currentEditorState.isSegmentTargetingEnabled
                }
            };
        }

        case TOGGLE_CORRECTIONS_EXPERIMENTS_EDITOR: {
            const { editorId } = action;
            const currentEditorState =
                state[editorId] || experimentsEditorInitialState;

            return {
                ...state,
                [editorId]: {
                    ...currentEditorState,
                    areCorrectionsEnabled: !currentEditorState.areCorrectionsEnabled
                }
            };
        }

        case EDIT_SEARCH_VALUE_EXPERIMENTS_EDITOR: {
            const { editorId } = action;
            const currentEditorState =
                state[editorId] || experimentsEditorInitialState;

            let newFilteredMap = currentEditorState.filteredMap;
            let newExpandedMap = currentEditorState.expandedMap;

            if (action.searchValue !== undefined) {
                const newMaps = TreeHelpers.getUpdatedBySearchTreeParams(
                    action.experiments,
                    action.searchValue
                );

                newFilteredMap = newMaps.filteredMap;
                newExpandedMap = newMaps.expandedMap;
            }

            return {
                ...state,
                [editorId]: {
                    ...currentEditorState,
                    filteredMap: newFilteredMap,
                    expandedMap: newExpandedMap,
                    searchValue: action.searchValue || ''
                }
            };
        }

        case SELECT_EXPERIMENT_TREE_NODE: {
            const { editorId, isSegment } = action;
            const currentEditorState =
                state[editorId] || experimentsEditorInitialState;
            const nodeId = action.node.id;

            const newSelectedMap = {
                ...currentEditorState.selectedMap,
                [nodeId]: !currentEditorState.selectedMap[nodeId]
            };

            let newDisabledMap = cloneDeep(currentEditorState.disabledMap);
            if (isSegment) {
                newDisabledMap = enableSiblingSegments(
                    action.experiments,
                    newDisabledMap,
                    action.node
                );
            } else {
                newDisabledMap = getDisabledExperimentsMapIfLimit(
                    action.experiments,
                    newDisabledMap,
                    newSelectedMap,
                    action.activeExperimentsLimit
                );
            }

            return {
                ...state,
                [editorId]: {
                    ...currentEditorState,
                    selectedMap: newSelectedMap,
                    disabledMap: newDisabledMap
                }
            };
        }

        case DESELECT_EXPERIMENT_TREE_NODE: {
            const { editorId, isSegment } = action;
            const currentEditorState =
                state[editorId] || experimentsEditorInitialState;
            const nodeId = action.node.id;
            const { disabledMap, selectedMap } = currentEditorState;

            let newDisabledMap = disabledMap;
            if (isSegment) {
                newDisabledMap = getDisabledSegmentsMapByExperiment(
                    action.experiments,
                    action.node,
                    selectedMap,
                    newDisabledMap
                );
            } else {
                newDisabledMap = enableExperiments(
                    action.experiments,
                    newDisabledMap
                );
            }

            return {
                ...state,
                [editorId]: {
                    ...currentEditorState,
                    selectedMap: {
                        ...selectedMap,
                        [nodeId]: !selectedMap[nodeId]
                    },
                    disabledMap: newDisabledMap
                }
            };
        }

        case TOGGLE_EXPERIMENT_TREE_NODE_EXPANSION: {
            const { editorId } = action;
            const currentEditorState =
                state[editorId] || experimentsEditorInitialState;
            const nodeId = action.node.id;

            return {
                ...state,
                [editorId]: {
                    ...currentEditorState,
                    expandedMap: {
                        ...currentEditorState.expandedMap,
                        [nodeId]: !currentEditorState.expandedMap[nodeId]
                    }
                }
            };
        }

        case CHANGE_CORRECTIONS_VALUE_EXPERIMENTS_EDITOR: {
            const { editorId } = action;
            const currentEditorState =
                state[editorId] || experimentsEditorInitialState;
            const { segmentId } = action;
            const { minRate, maxRate } = action;

            const validationErrors = validateRateCorrectionValue(
                action.correctionValue,
                { minRate, maxRate }
            );

            return {
                ...state,
                [editorId]: {
                    ...currentEditorState,
                    correctionsMap: {
                        ...currentEditorState.correctionsMap,
                        [segmentId]: {
                            value: {
                                ...action.correctionValue,
                                value: action.correctionValue.value
                            },
                            errors: validationErrors
                        }
                    }
                }
            };
        }

        default:
            return state;
    }
}
